import React, { useRef, useState, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/autoplay";

const TestimonialCard = ({ item, getInitials }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsReadMore, setNeedsReadMore] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      const { scrollHeight, clientHeight } = textRef.current;
      setNeedsReadMore(scrollHeight > clientHeight);
    }
  }, [item.text]);

  return (
    <div className={`new_testimonial_card ${isExpanded ? "expanded" : ""}`}>
      <div className="card_header">
        <div className="quote_icon_box">
          <i className="fa-solid fa-quote-left"></i>
        </div>
      </div>

      <div className="card_body">
        <motion.div
          animate={{ height: isExpanded ? "auto" : "150px" }} // Increased to 150px to fit 4 lines on desktop
          transition={{ duration: 0.4, ease: "easeInOut" }}
          style={{ overflow: "hidden" }}
        >
          <p
            ref={textRef}
            className={`testimonial_quote_text ${!isExpanded ? "clamped" : ""}`}
          >
            "{item.text}"
          </p>
        </motion.div>
        
        {needsReadMore && (
          <button
            className="read_more_btn"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "Show Less" : "Show More"}
          </button>
        )}
      </div>

      <div className="card_footer">
        <div className="user_profile_box">
          {item.image ? (
            <img src={item.image} alt={item.name} className="user_avatar_img" />
          ) : (
            <div className="initials_avatar">{getInitials(item.name)}</div>
          )}
          <div className="user_info">
            <h6 className="user_name_text">{item.name}</h6>
            <p className="user_position_text">{item.position}</p>
            {item.socials && item.socials.linkedin && (
              <a
                href={item.socials.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="testimonial_social_link"
              >
                <i className="fab fa-linkedin"></i>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Testimonials = () => {
  const [activeYear, setActiveYear] = useState("2026");
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true });

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const testimonialsData = {
    2026: [
      {
        text: "An outstanding and insightful event that brought together industry leaders to explore the future of MSB operations—from scaling payments across fiat and digital rails to the growing role of stablecoins as a treasury tool. The discussions offered valuable perspectives on trust, risk management, and the increasing focus of UK regulators on AI-driven KYC, KYB, and AML processes. Combined with thought-provoking keynote insights and excellent networking opportunities, the event truly reflected the strength, innovation, and collaborative spirit of the MSB community.",
        name: "Giordano Cortese",
        position: "First Rate Exchange Services Ltd. (Senior Manager)",
        image: "",
        socials: {
          linkedin: "https://www.linkedin.com/feed/update/urn:li:activity:7450902870859616256/?actorCompanyId=106083112",
        },
      },
      {
        text: "Pleased to represent Money Express Latvia–Estonia–Poland at the MSB Association UK Annual Members Meet 2026. The event delivered powerful insights into scaling payment operations across fiat and digital rails, with a strong emphasis on trust, risk awareness, and innovation. Key highlights included the growing adoption of stablecoins in payments, increasing regulatory focus on AI-driven KYC and AML processes, and the need for MSBs to stay proactive in an evolving landscape. Alongside thought-provoking discussions, the event also offered excellent networking opportunities, making it a truly valuable and forward-looking experience.",
        name: "Romāns Matuļevičs",
        position: "Money Express Latvia–Estonia–Poland",
        image: "",
        socials: {
          linkedin: "https://www.linkedin.com/feed/update/urn:li:activity:7450890905680703488/?actorCompanyId=106083112",
        },
      },
      {
        text: "Thank you to the MSB Association UK for partnering on such a well-curated event, bringing together members, clients, and industry peers for an evening that truly reflected the strength of the MSB ecosystem. It was a pleasure reconnecting with long-standing partners and businesses we’ve supported for over a decade—relationships built on trust, consistency, and a deep understanding of the sector. The discussions reinforced the importance of reliable, forward-thinking payments partners, especially as the industry evolves with new technologies and regulatory expectations. Combined with excellent networking and a great atmosphere, the event was both meaningful and memorable.",
        name: "Clear Junction",
        position: "Strategic Partner",
        image: "",
        socials: {
          linkedin: "https://www.linkedin.com/feed/update/urn:li:activity:7450824529465081856/?actorCompanyId=106083112",
        },
      },
      {
        text: "Thank you for having me, it was great to connect with some of our clients and members of MSB whilst also being able to discuss my favourite topic. Fantastic event",
        name: "Teresa Cameron FCT, CGMA",
        position: "CEO, Clear Junction",
        image: "",
        socials: {
          linkedin: "https://www.linkedin.com/feed/update/urn:li:activity:7450875796522504192?commentUrn=urn%3Ali%3Acomment%3A%28activity%3A7450875796522504192%2C7450932288084897792%29&dashCommentUrn=urn%3Ali%3Afsd_comment%3A%287450932288084897792%2Curn%3Ali%3Aactivity%3A7450875796522504192%29",
        },
      },
      {
        text: "Thanks for a great evening, too venue so close to St Paul’s and an informative panel on Stablecoins and I particularly enjoyed Saima Hanif KC’s keynote. Some incredible case studies in “what not to do”",
        name: "Steve Allen",
        position: "VP of Sales, Clear Junction",
        image: "",
        socials: {
          linkedin: "https://www.linkedin.com/feed/update/urn:li:ugcPost:7450863000661667840?commentUrn=urn%3Ali%3Acomment%3A%28ugcPost%3A7450863000661667840%2C7450985071123169280%29&dashCommentUrn=urn%3Ali%3Afsd_comment%3A%287450985071123169280%2Curn%3Ali%3AugcPost%3A7450863000661667840%29",
        },
      },
      {
        text: "Thank you, for inviting me to such a wonderful event. I truly enjoyed it a lot and learned so much. I’m looking forward to the next one.",
        name: "Abdirahman Ahmednur",
        position: "Dimond General trading",
        image: "",
      },
    ],
    2025: [
      {
        text: "Great to attend the inaugural gathering of the MSB Association UK members at Mc Dermott Will & Emery UK LLP, Bishopsgate, yesterday. An initiative bringing together Money Service Business owners and industry professionals for a progressive, resilient and compliant MSB ecosystem for the future. Thanks to keynote speakers... it was a great evening! To the next one! Ad Astra Per Aspera!",
        name: "Giordano Cortese",
        position: "First Rate Exchange Services Ltd. (Senior Manager, Partnerships & Client Acquisition)",
        image: "",
        socials: {
          linkedin: "https://www.linkedin.com/posts/activity-7341775464719433730-AZKI?utm_source=share&utm_medium=member_desktop&rcm=ACoAACNPxx0BWbuSxjsfoVlUlizj-ngx4y6N8DY",
        },
      },
      {
        text: "First and foremost, I want to say how impressed I was with the inaugural event—it was truly an excellent initiative. Bringing MSBs together under one association to discuss shared challenges and support the industry is a remarkable achievement. I felt honoured to be part of this historic moment.",
        name: "Sampaio Marques",
        position: "GCC (Executive Director)",
        image: "",
        socials: {
          linkedin: "https://www.linkedin.com/posts/activity-7341775464719433730-AZKI?utm_source=share&utm_medium=member_desktop&rcm=ACoAACNPxx0BWbuSxjsfoVlUlizj-ngx4y6N8DY",
        },
      },
      {
        text: " I am glad to be the part of MSB Association, Thank you MSB Association. Special Thanks to Ajinkya and Vishal Bhai.",
        name: "Sai Teja",
        position: "Rupy Global Solutions Limited (CEO)",
        image: "",
        socials: {
          linkedin: "https://www.linkedin.com/posts/activity-7341775464719433730-AZKI?utm_source=share&utm_medium=member_desktop&rcm=ACoAACNPxx0BWbuSxjsfoVlUlizj-ngx4y6N8DY",
        },
      },
      {
        text: "Hi vishal great event last night congratulations! Well done to you and your team! Speak soon.",
        name: "Harry Deans",
        position: "LinkFX (MSB Development)",
        image: "",
        socials: {
          linkedin: "https://www.linkedin.com/posts/activity-7341775464719433730-AZKI?utm_source=share&utm_medium=member_desktop&rcm=ACoAACNPxx0BWbuSxjsfoVlUlizj-ngx4y6N8DY",
        },
      },
      {
        text: "Great Event enjoyed a lot Proud of you",
        name: "Chamika",
        position: "FX Currency London (Director)",
        image: "",
        socials: {
          linkedin: "https://www.linkedin.com/posts/activity-7341775464719433730-AZKI?utm_source=share&utm_medium=member_desktop&rcm=ACoAACNPxx0BWbuSxjsfoVlUlizj-ngx4y6N8DY",
        },
      },
      {
        text: "That’s a great initiative, and very nice event.",
        name: "Diego Colucci",
        position: "Real Money Transfer (Data & Operations Manager)",
        image: "",
        socials: {
          linkedin: "https://www.linkedin.com/posts/activity-7341775464719433730-AZKI?utm_source=share&utm_medium=member_desktop&rcm=ACoAACNPxx0BWbuSxjsfoVlUlizj-ngx4y6N8DY",
        },
      },
    ],
  };

  return (
    <>
      <section
        className="funfact_section section_space"
        style={{ background: "#ffffff" }}
      >
        <div className="container">
          <div className="heading_block text-center mb-5">
            <motion.div
              ref={sectionRef}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { duration: 0.8 } },
              }}
            >
              <div className="badge style_2 text-uppercase mb-3">
                <i className="fa-solid fa-circle text-primary"></i> OUR
                TESTIMONIALS{" "}
                <i className="fa-solid fa-circle text-primary"></i>
              </div>
              <h2 className="heading_text mb-4 fw-bold">
                Celebrating the Experiences of Our Members
              </h2>

              <div className="merged_tabs_container">
                {["2026", "2025"].map((year) => (
                  <button
                    key={year}
                    className={`merged_tab_btn ${activeYear === year ? "active" : ""}`}
                    onClick={() => setActiveYear(year)}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="testimonials_slider_main">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeYear}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <div className="swiper_container_wrap">
                  <Swiper
                    modules={[Pagination, Navigation, Autoplay]}
                    spaceBetween={30}
                    slidesPerView={1}
                    navigation={{
                      nextEl: ".swiper-button-next-custom",
                      prevEl: ".swiper-button-prev-custom",
                    }}
                    autoplay={{ delay: 6000, disableOnInteraction: false }}
                    loop={testimonialsData[activeYear].length > 1}
                    observer={true}
                    observeParents={true}
                    className="custom_testimonial_swiper"
                  >
                    {testimonialsData[activeYear].map((item, index) => (
                      <SwiperSlide key={index}>
                        <TestimonialCard item={item} getInitials={getInitials} />
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  {/* Navigation Arrows Moved Outside Slide but Inside Wrapper for Shared Positioning */}
                  <div className="slider_controls_outer">
                    <div className="swiper-button-prev-custom">
                      <i className="fa-solid fa-chevron-left"></i>
                    </div>
                    <div className="swiper-button-next-custom">
                      <i className="fa-solid fa-chevron-right"></i>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      <style jsx="true">{`
        .merged_tabs_container {
          display: inline-flex;
          background: #f1f1f1;
          padding: 8px;
          border-radius: 100px;
          margin-bottom: 20px;
          position: relative;
          box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.06);
        }

        .merged_tab_btn {
          border: none;
          background: transparent;
          padding: 12px 45px;
          border-radius: 100px;
          font-weight: 700;
          font-size: 17px;
          color: #777;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          cursor: pointer;
          min-width: 140px;
          position: relative;
          z-index: 2;
        }

        .merged_tab_btn.active {
          background: #FBA834;
          color: #fff;
          box-shadow: 0 8px 25px rgba(251, 168, 52, 0.35);
        }

        .testimonials_slider_main {
          overflow: visible;
          padding: 20px 0; /* Reduced from 40px */
          min-height: 620px;
        }

        .swiper_container_wrap {
          position: relative;
          padding: 10px 0; /* Reduced from 20px */
          overflow: visible !important;
        }

        :global(.custom_testimonial_swiper) {
          padding: 40px 20px 80px !important;
          margin: -40px -20px -40px !important;
          overflow: visible !important;
        }

        :global(.custom_testimonial_swiper .swiper-wrapper) {
          overflow: visible !important;
        }

        .new_testimonial_card {
          background: #fff;
          padding: 60px 80px;
          border-radius: 35px;
          border: 2px solid #FBA834;
          position: relative;
          margin-bottom: 20px;
          min-height: 480px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          transition: all 0.4s ease;
        }

        .quote_icon_box {
          color: #222;
          font-size: 32px;
          opacity: 0.9;
          margin-bottom: 20px;
        }

        .testimonial_quote_text {
          font-size: 1.3rem;
          line-height: 1.7;
          color: #555;
          font-style: italic;
          font-weight: 400;
          margin-bottom: 10px;
          max-width: 95%;
        }

        .testimonial_quote_text.clamped {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .read_more_btn {
          background: transparent;
          border: none;
          color: #FBA834;
          font-weight: 700;
          font-size: 1rem;
          padding: 0;
          margin: 15px 0 30px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
          transition: 0.3s;
        }

        .read_more_btn:hover {
          text-decoration: underline;
          opacity: 0.8;
        }

        .card_footer {
          border-top: 1px solid #eee;
          padding-top: 35px;
        }

        .user_profile_box {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .user_avatar_img,
        .initials_avatar {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          object-fit: cover;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          font-weight: 800;
          color: #fff;
          background: #FBA834;
          box-shadow: 0 10px 20px rgba(251, 168, 52, 0.2);
        }

        .user_name_text {
          font-size: 1.2rem;
          font-weight: 800;
          color: #111;
          margin-bottom: 5px;
        }

        .user_position_text {
          font-size: 0.95rem;
          color: #FBA834;
          font-weight: 600;
          margin-bottom: 0;
        }

        .testimonial_social_link {
          color: #0077b5;
          font-size: 1.2rem;
          display: inline-block;
          margin-top: 8px;
          transition: 0.3s;
        }

        .testimonial_social_link:hover {
          color: #00507a;
          transform: scale(1.15);
        }

        .swiper-button-prev-custom,
        .swiper-button-next-custom {
          position: absolute;
          top: 55%; /* Centered better with card content */
          transform: translateY(-50%);
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #FBA834;
          font-size: 32px; /* Slightly larger for clarity */
          z-index: 100;
          transition: 0.3s;
        }

        .swiper-button-prev-custom {
          left: -60px;
        }
        .swiper-button-next-custom {
          right: -60px;
        }

        .swiper-button-prev-custom:hover,
        .swiper-button-next-custom:hover {
          transform: translateY(-50%) scale(1.2);
          text-shadow: 0 0 10px rgba(251, 168, 52, 0.2);
        }

        @media (max-width: 1200px) {
          .new_testimonial_card {
            padding: 50px 60px;
            min-height: 450px;
          }
          .testimonial_quote_text {
            font-size: 1.25rem;
          }
        }

        @media (max-width: 991px) {
          .new_testimonial_card {
            padding: 40px 40px;
            min-height: auto;
          }
          .testimonial_quote_text {
            font-size: 1.15rem;
            max-width: 100%;
          }
          
          .slider_controls_outer {
            display: flex;
            justify-content: center;
            gap: 20px;
            margin-top: 20px;
          }

          .swiper-button-prev-custom,
          .swiper-button-next-custom {
            position: static;
            transform: none !important;
            display: flex !important;
            color: #FBA834; /* Ensure brand color on mobile */
          }

          .swiper-button-prev-custom:hover,
          .swiper-button-next-custom:hover {
            transform: scale(1.1) !important; /* Fix: Remove translateY(-50%) on mobile */
          }
          
          .testimonials_slider_main {
            min-height: auto;
          }
        }

        @media (max-width: 576px) {
          .new_testimonial_card {
            padding: 30px 20px;
          }
          .merged_tab_btn {
            padding: 10px 25px;
            min-width: 110px;
            font-size: 15px;
          }
          .user_avatar_img,
          .initials_avatar {
            width: 60px;
            height: 60px;
            font-size: 20px;
          }
        }
      `}</style>
    </>
  );
};

export default Testimonials;